import type { GpsPermissionState, LocalQueuedScan } from "../../shared/src/scan-contract.ts";

import type { QrCaptureLocation } from "./qr-capture.ts";
import { validateFreeformPresenceNote } from "./freeform-presence.ts";

export interface QueuedManualFallbackScanInput {
  checkpointId: string | null;
  clientEventId: string;
  deviceTime: string;
  deviceId: string;
  appVersion: string;
  networkOnline: boolean;
  dwellSeconds?: number | null;
  /** Optional for a pinned checkpoint and required for free-form presence. */
  guardNote?: string | null;
  routeAssignmentId?: string | null;
  location: QrCaptureLocation;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function createQueuedManualFallbackScan(input: QueuedManualFallbackScanInput): LocalQueuedScan {
  const checkpointId = normalizeCheckpointId(input.checkpointId);
  const location = normalizeLocation(input.location);
  const guardNote = normalizeOptionalGuardNote(input.guardNote);

  if (checkpointId === null) {
    const noteError = validateFreeformPresenceNote(guardNote ?? "");
    if (noteError !== null) {
      throw new Error(noteError);
    }
  }

  return {
    lastError: null,
    localState: "queued",
    payload: {
      appVersion: input.appVersion,
      ...(checkpointId !== null ? { checkpointId } : {}),
      clientEventId: input.clientEventId,
      deviceId: input.deviceId,
      deviceTime: input.deviceTime,
      gpsAccuracyMeters: location.accuracyMeters,
      gpsPermission: input.location.permission,
      ...(guardNote !== null ? { guardNote } : {}),
      lat: location.lat,
      lng: location.lng,
      offlineCreated: !input.networkOnline,
      presenceKind: "manual",
      scanMethod: "MANUAL",
      token: "",
      verification: {
        dwellSeconds: normalizeDwellSeconds(input.dwellSeconds),
        evidenceProvided: guardNote !== null ? ["note"] : [],
        gpsDistanceMeters: null,
        methodUsed: manualVerificationMethod(input.location.permission),
        mockLocation: false,
        tokenBound: null,
        tokenStatus: null,
      },
    },
    retryCount: 0,
    routeAssignmentId: input.routeAssignmentId ?? null,
    serverScanId: null,
    serverValidationStatus: null,
  };
}

function normalizeOptionalGuardNote(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeDwellSeconds(value: number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.round(value);
}

function normalizeCheckpointId(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const checkpointId = value.trim();
  if (!UUID_RE.test(checkpointId)) {
    throw new Error("Manual fallback requires a valid checkpoint id.");
  }
  return checkpointId.toLowerCase();
}

function manualVerificationMethod(permission: GpsPermissionState): "gps_geofence" | "manual" {
  return permission === "granted" ? "gps_geofence" : "manual";
}

function normalizeLocation(location: QrCaptureLocation): Pick<QrCaptureLocation, "lat" | "lng" | "accuracyMeters"> {
  if (location.permission !== "granted") {
    return { accuracyMeters: null, lat: null, lng: null };
  }

  return {
    accuracyMeters: location.accuracyMeters,
    lat: location.lat,
    lng: location.lng,
  };
}
